import { z } from 'zod';
import { idSchema } from './common.js';
import { invoiceStatusSchema } from './invoice.js';
import { loadStatusSchema, loadStopTypeSchema } from './load.js';
import { vehicleTypeSchema } from './vehicle.js';
/** Customer-portal route params. Bodies reuse the shared auth schemas
 *  (otpRequestBodySchema / otpVerifyBodySchema / refreshBodySchema /
 *  logoutBodySchema). */
export const portalFleetParamSchema = z.object({ fleetId: idSchema });
export const portalInviteTokenParamSchema = z.object({
    token: z.string().trim().min(16).max(128),
});
/** Caddy on-demand-TLS `ask` query (0047): the hostname it wants a cert for. */
export const portalTlsCheckQuerySchema = z.object({
    domain: z.string().trim().min(1).max(255),
});
// -------- Self-serve custom domains (0065) --------
export const claimPortalDomainBodySchema = z
    .object({
    hostname: z.string().trim().min(4).max(253),
    /** Apex A-record fallback — explicit opt-in, documented as fragile. */
    allowApex: z.boolean().optional(),
})
    .strict();
export const portalDomainHostParamSchema = z.object({
    fleetId: idSchema,
    hostname: z.string().trim().min(4).max(253),
});
// -------- Portal data endpoints (E1–E4) --------
// No fleetId in any path here: the fleet comes from the customer's token
// (`customerAuth.fleetId`), never from the URL. A customer cannot even name
// another fleet, let alone reach one.
export const portalLoadParamSchema = z.object({ loadId: idSchema });
export const portalInvoiceParamSchema = z.object({ invoiceId: idSchema });
export const portalPodParamSchema = z.object({ loadId: idSchema, podId: idSchema });
const phoneE164Schema = z
    .string()
    .trim()
    .regex(/^\+?[1-9]\d{6,14}$/, 'Phone must be E.164 (e.g. +447700900123)')
    .max(16);
const isoCountrySchema = z
    .string()
    .trim()
    .toUpperCase()
    .pipe(z.string().regex(/^[A-Z]{2}$/, 'country must be ISO-2 (e.g. GB)'));
/**
 * An item the customer is booking, nested under the stop it belongs to.
 *
 * `load_items.load_stop_id` is NOT NULL, and at booking time the customer has
 * no stop ids to reference — so items ride inside their stop rather than
 * carrying a `loadStopId` the caller couldn't know. The service wires the real
 * ids up after the stops land.
 */
export const portalBookingItemSchema = z
    .object({
    description: z.string().trim().min(1).max(200),
    quantity: z.number().int().min(1).max(100000).optional(),
    unit: z.string().trim().max(40).nullable().optional(),
    weightKg: z.number().finite().min(0).max(100000).nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
})
    .strict();
/**
 * A stop on a customer booking. Note what is NOT accepted: `lat`/`lng`,
 * `customerAddressId`, `sequence`, and `status`. Coordinates are the carrier's
 * to resolve (the service geocodes the postcode, same as the dispatcher path),
 * customer_addresses belong to the carrier's address book, and sequence is
 * taken from array order so a customer can't interleave someone else's stops.
 */
export const portalBookingStopSchema = z
    .object({
    type: loadStopTypeSchema,
    line1: z.string().trim().min(1).max(160),
    line2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    country: isoCountrySchema.optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactPhoneE164: phoneE164Schema.nullable().optional(),
    windowStartAt: z.string().datetime().nullable().optional(),
    windowEndAt: z.string().datetime().nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    instructions: z.string().trim().max(2000).nullable().optional(),
    items: z.array(portalBookingItemSchema).max(200).optional(),
})
    // Strict for the same reason as the outer body: an attempt to set `lat`,
    // `status`, or `customerAddressId` on a stop is refused rather than
    // silently stripped. A plain z.object() would drop them quietly, which is
    // safe but tells the caller nothing.
    .strict();
/**
 * Booking intake (E1).
 *
 * `.strict()` is load-bearing, not tidiness: it turns an attempt to set
 * `totalPence`, `assignedDriverUserId`, `status`, or `notesInternal` into a
 * 400 instead of a silently-ignored field. Pricing, crewing, and internal
 * notes are the dispatcher's alone (locked decision 7 — no self-serve
 * quoting), and a customer should be told plainly that they cannot set them.
 */
export const portalCreateLoadBodySchema = z
    .object({
    customerReference: z.string().trim().max(80).nullable().optional(),
    customerPo: z.string().trim().max(80).nullable().optional(),
    /** Free text from the customer to the carrier. Lands in notes_customer. */
    notesForCarrier: z.string().trim().max(4000).nullable().optional(),
    /** A request, not a promise — the dispatcher decides what actually runs. */
    vehicleType: vehicleTypeSchema.nullable().optional(),
    tailLift: z.boolean().optional(),
    stops: z.array(portalBookingStopSchema).min(2).max(50),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.stops.some((s) => s.type === 'pickup')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['stops'],
            message: 'A booking needs at least one collection stop',
        });
    }
    if (!v.stops.some((s) => s.type === 'dropoff')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['stops'],
            message: 'A booking needs at least one delivery stop',
        });
    }
    v.stops.forEach((s, i) => {
        if (s.windowStartAt &&
            s.windowEndAt &&
            new Date(s.windowEndAt) < new Date(s.windowStartAt)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['stops', i, 'windowEndAt'],
                message: 'Window end must not be before window start',
            });
        }
    });
});
export const portalListLoadsQuerySchema = z.object({
    status: loadStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * Invoice list filter. `draft` is absent from this enum on purpose — a draft
 * is the carrier's working copy, so it is not merely filtered out downstream,
 * it is unnameable (E3).
 */
export const portalVisibleInvoiceStatusSchema = invoiceStatusSchema.exclude([
    'draft',
]);
export const portalListInvoicesQuerySchema = z.object({
    status: portalVisibleInvoiceStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// -------- Account (M5) --------
/**
 * Billing details card — feeds invoice headers (M5). All nullable-optional:
 * a field left blank clears it, a field omitted is untouched.
 */
export const portalBillingBodySchema = z
    .object({
    companyName: z.string().trim().min(1).max(160).optional(),
    billingEmail: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
    billingAddressLine1: z.string().trim().max(160).nullable().optional(),
    billingAddressLine2: z.string().trim().max(160).nullable().optional(),
    billingCity: z.string().trim().max(80).nullable().optional(),
    billingPostcode: z.string().trim().max(16).nullable().optional(),
    vatNumber: z.string().trim().max(20).nullable().optional(),
})
    .strict();
/** Your-details card. Sign-in email changes ride the re-verify pair, not this. */
export const portalMePatchBodySchema = z
    .object({
    fullName: z.string().trim().min(1).max(120).nullable().optional(),
    phoneE164: phoneE164Schema.nullable().optional(),
})
    .strict();
// -------- Sign-in email change (design brief §4/§5) --------
/**
 * Change of sign-in email, request half: we email a code to the NEW address.
 * The change itself happens only at confirm, after that code is verified —
 * possession of the code is proof of control of the new inbox.
 */
export const portalEmailChangeRequestBodySchema = z
    .object({ newEmail: z.string().trim().toLowerCase().email().max(254) })
    .strict();
export const portalEmailChangeConfirmBodySchema = z
    .object({
    newEmail: z.string().trim().toLowerCase().email().max(254),
    code: z.string().trim().regex(/^\d{6}$/, 'Six digits'),
})
    .strict();
// -------- Estimate (design brief §4/§5) --------
/**
 * The vehicle choices the portal offers on /book — the brief's tariff ladder
 * plus the two priced-on-confirmation sizes. A strict subset of the full
 * fleet enum: a customer can't ask the estimator about an artic.
 */
export const portalEstimateVehicleSchema = vehicleTypeSchema.extract([
    'cycle',
    'cargo_bike',
    'motorcycle',
    'car',
    'small_van',
    'mwb',
    'lwb',
    'luton',
    'xlwb',
    'jumbo_luton',
]);
const estimatePostcodeSchema = z.string().trim().min(5).max(10);
/**
 * Estimate request for the /book panel. Distance is collection → delivery
 * only (v1 keeps the brief's "distance between postcodes"); added stops ride
 * as a count feeding the from-£15-per-stop line, not extra legs. `scheduledAt`
 * absent/null = ASAP, so out-of-hours is judged on the server clock.
 */
export const portalEstimateBodySchema = z
    .object({
    vehicleType: portalEstimateVehicleSchema,
    collectionPostcode: estimatePostcodeSchema,
    deliveryPostcode: estimatePostcodeSchema,
    extraStops: z.number().int().min(0).max(8).default(0),
    scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
})
    .strict();
/** Mint a tracking link for the customer's own load (E4). */
export const portalShareLoadBodySchema = z
    .object({
    label: z.string().trim().max(80).nullable().optional(),
})
    .strict();
// -------- Domain purchase rail (U3) --------
export const domainSearchBodySchema = z
    .object({
    /** A brand seed ("harbour couriers") or a pasted domain/URL
     *  ("https://www.breakernetwork.co.uk") — the service parses forgivingly
     *  and fans out across the enabled TLDs, exact typed domain first. */
    query: z.string().trim().min(2).max(253),
})
    .strict();
export const createDomainOrderBodySchema = z
    .object({
    domain: z.string().trim().toLowerCase().min(4).max(253),
    registrant: z
        .object({
        firstName: z.string().trim().min(1).max(125),
        lastName: z.string().trim().min(1).max(125),
        organization: z.string().trim().min(1).max(255).optional(),
        email: z.string().trim().email().max(255),
        address1: z.string().trim().min(1).max(255),
        address2: z.string().trim().min(1).max(255).optional(),
        city: z.string().trim().min(1).max(255),
        postalCode: z.string().trim().min(1).max(16),
        /** UK-only v1; the server normalises to +44.xxxxxxxxxx. */
        phone: z.string().trim().min(7).max(20),
    })
        .strict(),
    /** Required registration consent — "use these details to register in my
     *  name". Must be true; the server rejects anything else. */
    registrationConsent: z.boolean(),
    /** Optional WHOIS-privacy preference, default on. Honoured only where the
     *  TLD offers a registrar privacy proxy (.com); .uk is always public. */
    whoisPrivacy: z.boolean().optional().default(true),
    /** Where Stripe returns to: 'settings' (the door, default) or 'wizard'
     *  (in-signup purchase) — drives success/cancel URLs so the wizard
     *  resumes at the polling step instead of Settings. */
    returnTo: z.enum(['settings', 'wizard']).optional(),
})
    .strict();
export const domainOrderIdParamSchema = z.object({
    fleetId: idSchema,
    orderId: idSchema,
});
//# sourceMappingURL=portal.js.map